import * as React from "react";
import {
  filterMethods, collectTags, sortMethods, SORT_ORDERS,
  type Method, type MethodFilter, type MethodMode, type SortOrder,
} from "@klapzo/kit-core";
import { listMethods, useRemote } from "../kitApi.js";
import { getLocale, t, type Locale } from "../i18n.js";
import { KitShell, RemoteError, FavouriteButton } from "../components.js";
import { addBlock } from "../agendaStore.js";
import { loadFavourites, toggleFavourite } from "../favouritesStore.js";
import { loadFilterSets, saveFilterSet, deleteFilterSet, type FilterSet } from "../filterSetsStore.js";

const MODES: MethodMode[] = ["online", "presencial", "hibrido"];

export function Library(): React.ReactElement {
  const locale = getLocale();
  const remote = useRemote<Method[]>((signal) => listMethods(locale, signal), [locale]);

  return (
    <KitShell>
      <h1 className="font-display text-display-md">{t("library.title")}</h1>
      {remote.state === "loading" && <p>{t("kit.loading")}</p>}
      {remote.state === "error" && <RemoteError message={t("kit.error")} retry={t("kit.retry")} />}
      {remote.state === "ready" && <Browser all={remote.data} locale={locale} />}
    </KitShell>
  );
}

/** Filtering stays client-side over the fetched set: it is one small list and the chips are instant. */
function Browser({ all, locale }: { all: Method[]; locale: Locale }): React.ReactElement {
  const allTags = React.useMemo(() => collectTags(all), [all]);
  const [query, setQuery] = React.useState("");
  const [mode, setMode] = React.useState<MethodMode | "">("");
  const [tags, setTags] = React.useState<string[]>([]);
  const [favourites, setFavourites] = React.useState<Set<string>>(() => loadFavourites());
  const [onlyFavourites, setOnlyFavourites] = React.useState(false);
  const [sort, setSort] = React.useState<SortOrder>("title");
  const [sets, setSets] = React.useState<FilterSet[]>(() => loadFilterSets());

  const current = (name: string): FilterSet => ({ name, query, mode, tags, onlyFavourites, sort });
  const apply = (s: FilterSet): void => {
    setQuery(s.query);
    setMode(s.mode);
    setTags(s.tags);
    setOnlyFavourites(s.onlyFavourites);
    setSort(s.sort);
  };
  const saveCurrent = (): void => {
    // prompt() rather than a modal: this is a power-user affordance on a browsing screen, and a
    // bespoke dialog here would be more code than the feature.
    const name = window.prompt(t("library.saveSetPrompt"))?.trim();
    if (name) setSets(saveFilterSet(current(name)));
  };

  const filter: MethodFilter = {};
  if (query) filter.query = query;
  if (mode) filter.mode = mode;
  if (tags.length) filter.tags = tags;
  // Favourites narrow the filtered set rather than joining MethodFilter: they are a client-side
  // preference over slugs, not a property of a method, so kit-core has no business knowing them.
  const matched = filterMethods(all, filter);
  const narrowed = onlyFavourites ? matched.filter((m) => favourites.has(m.slug)) : matched;
  const results = sortMethods(narrowed, sort, locale);

  const toggleTag = (tag: string): void => setTags((prev) => (prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag]));

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("library.search")} className="flex-1 rounded-md border border-border p-3" />
          <select value={mode} onChange={(e) => setMode(e.target.value as MethodMode | "")} className="rounded-md border border-border p-3">
            <option value="">{t("library.allModes")}</option>
            {MODES.map((m) => (
              <option key={m} value={m}>{t(`mode.${m}`)}</option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOrder)}
            aria-label={t("library.sort")}
            className="rounded-md border border-border p-3"
          >
            {SORT_ORDERS.map((o) => (
              <option key={o} value={o}>{t(`sort.${o}`)}</option>
            ))}
          </select>
          <button
            onClick={() => setOnlyFavourites((v) => !v)}
            aria-pressed={onlyFavourites}
            className={`rounded-md border border-border px-3 text-body-sm ${onlyFavourites ? "bg-primary text-primary-foreground" : ""}`}
          >
            {"\u2605"} {t("library.onlyFavourites")}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={`rounded-full px-3 py-1 text-body-sm ${tags.includes(tag) ? "bg-primary text-primary-foreground" : "bg-surface"}`}
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-body-sm">
          <button onClick={saveCurrent} className="rounded-md border border-border px-3 py-1">
            {t("library.saveSet")}
          </button>
          {sets.map((s) => (
            <span key={s.name} className="flex items-center gap-1 rounded-full bg-surface px-2 py-1">
              <button onClick={() => apply(s)} className="text-accent">{s.name}</button>
              <button
                onClick={() => setSets(deleteFilterSet(s.name))}
                aria-label={`${t("library.deleteSet")} ${s.name}`}
                className="text-muted"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      </div>

      <p className="text-body-sm text-muted" role="status">
        {results.length} {t(results.length === 1 ? "library.countOne" : "library.count")}
      </p>

      {results.length === 0 && <p>{t("library.empty")}</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        {results.map((m) => (
          <div key={m.id} className="flex flex-col gap-2 rounded-md border border-border p-4">
            <a href={`/kit/m/${m.slug}`} className="font-display text-display-sm">{m.title}</a>
            <div className="flex flex-wrap gap-2 text-body-sm">
              <span className="rounded-md bg-surface px-2 py-1">{t(`mode.${m.mode}`)}</span>
              {m.durationMin != null && <span className="rounded-md bg-surface px-2 py-1">{m.durationMin} {t("planner.min")}</span>}
              {m.groupSize && <span className="rounded-md bg-surface px-2 py-1">👥 {m.groupSize}</span>}
            </div>
            <div className="mt-auto flex items-center justify-between">
              <div className="flex items-center gap-2">
                <a href={`/kit/m/${m.slug}`} className="text-accent underline text-body-sm">→</a>
                <FavouriteButton
                  active={favourites.has(m.slug)}
                  onToggle={() => setFavourites(toggleFavourite(m.slug))}
                  label={t(favourites.has(m.slug) ? "detail.unfavourite" : "detail.favourite")}
                />
              </div>
              <button onClick={() => addBlock(m)} className="rounded-md bg-secondary px-3 py-1 text-secondary-foreground text-body-sm">
                {t("library.add")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
